import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import i18n, { currentLocale } from '../../../i18n';
import { dateLocale } from '../../../lib/localePrefs';
import { SEV_META } from '../data/insights';
import type {
  LockEventKind,
  LockTimeline,
  RestaurantSettingsProfile,
  SettingCell,
  SettingsAuditPayload,
  SettingsColumn,
  SettingsIssueGroup,
  SettingStatus,
  ShiftState,
} from '../types';
import { Icon } from './shared/Icon';

const STATUS_COLOR: Record<SettingStatus, string> = {
  ok: 'var(--st-steady)',
  warn: 'var(--st-busy)',
  alert: 'var(--st-alert)',
};

function stateLabel(state: ShiftState): string {
  return i18n.t(`settings:health.state.${state}`);
}

function stateTip(state: ShiftState): string {
  return i18n.t(`settings:health.${state}`);
}

function lockKindLabel(kind: LockEventKind): string {
  return i18n.t(`settings:health.${kind}`);
}

function formatLockDate(iso: string): string {
  const d = new Date(`${iso}T12:00:00Z`);
  return d.toLocaleDateString(dateLocale(currentLocale()), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function LockTimelineTip({
  timeline,
  headline,
}: {
  timeline: LockTimeline;
  headline?: string;
}) {
  const { t } = useTranslation('settings');
  const renderEvent = (event: LockTimeline['past'][number], key: string) => (
    <li key={key}>
      {formatLockDate(event.date)}
      {event.shiftName ? ` · ${event.shiftName}` : ''}
      {event.time ? ` · ${event.time}` : ''}
      {' · '}
      {lockKindLabel(event.kind)}
    </li>
  );

  return (
    <div className="sm-lock-tip">
      {headline ? <div className="sm-lock-tip-head">{headline}</div> : null}
      {timeline.future.length > 0 ? (
        <>
          <div className="sm-lock-tip-label">{t('health.upcomingLab')}</div>
          <ul>
            {timeline.future.map((event, i) => renderEvent(event, `f-${i}`))}
          </ul>
        </>
      ) : null}
      {timeline.past.length > 0 ? (
        <>
          <div className="sm-lock-tip-label">{t('health.recentLab')}</div>
          <ul>
            {timeline.past.map((event, i) => renderEvent(event, `p-${i}`))}
          </ul>
        </>
      ) : null}
    </div>
  );
}

type TipPayload =
  | string
  | {
      text?: string;
      content?: React.ReactNode;
      wide?: boolean;
      placement?: 'above' | 'right';
    };

// ---- Tooltip: a cursor-anchored bubble rendered into <body> so it is never
// clipped by the matrix's scroll/overflow containers (and unaffected by the
// dashboard's root zoom, since it follows the mouse coordinates). ----
type TipApi = {
  show: (e: React.MouseEvent, payload?: TipPayload) => void;
  hide: () => void;
};
const TipCtx = createContext<TipApi>({ show: () => {}, hide: () => {} });
const useTip = () => useContext(TipCtx);

function Dot({ status }: { status: SettingStatus }) {
  return (
    <span className="sm-dot" style={{ background: STATUS_COLOR[status] }} />
  );
}

function Cell({ cell }: { cell?: SettingCell }) {
  const tip = useTip();
  if (!cell) {
    return <span className="sm-cell">{'\u2013'}</span>;
  }
  const hasTip = !!cell.tip || !!cell.lockTimeline;
  const showTip = (e: React.MouseEvent) => {
    if (cell.lockTimeline) {
      tip.show(e, {
        content: (
          <LockTimelineTip
            timeline={cell.lockTimeline}
            headline={cell.tip}
          />
        ),
        wide: true,
        placement: 'right',
      });
      return;
    }
    if (cell.tip) {
      tip.show(e, cell.tip);
    }
  };
  return (
    <span
      className="sm-cell"
      data-st={cell.status}
      data-tip={hasTip ? '' : undefined}
      onMouseEnter={hasTip ? showTip : undefined}
      onMouseMove={hasTip ? showTip : undefined}
      onMouseLeave={hasTip ? tip.hide : undefined}
    >
      {cell.display}
    </span>
  );
}

// Consecutive columns that share a group, for the two-level matrix header.
function groupSpans(columns: SettingsColumn[]) {
  const out: { label: string; span: number }[] = [];
  for (const c of columns) {
    const last = out[out.length - 1];
    if (last && last.label === c.groupLabel) last.span += 1;
    else out.push({ label: c.groupLabel, span: 1 });
  }
  return out;
}

function cellFor(cells: SettingCell[], key: string): SettingCell | undefined {
  return cells.find(c => c.key === key);
}

function SettingsMatrix({
  columns,
  restaurants,
}: {
  columns: SettingsColumn[];
  restaurants: RestaurantSettingsProfile[];
}) {
  const tip = useTip();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const groups = useMemo(() => groupSpans(columns), [columns]);
  const toggle = (id: string) =>
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div className="sm-scroll">
      <table className="sm-table">
        <thead>
          <tr>
            <th className="sm-th-out" rowSpan={2}>
              Outlet
            </th>
            {groups.map(g => (
              <th key={g.label} className="sm-th-grp" colSpan={g.span}>
                {g.label}
              </th>
            ))}
          </tr>
          <tr>
            {columns.map(c => (
              <th
                key={c.key}
                className="sm-th-col"
                onMouseEnter={e => tip.show(e, c.tip)}
                onMouseMove={e => tip.show(e, c.tip)}
                onMouseLeave={tip.hide}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {restaurants.map(r => {
            const open = expanded.has(r.outletId);
            const expandable = r.shiftCount > 1;
            return (
              <React.Fragment key={r.outletId}>
                <tr
                  className={`sm-row${open ? ' open' : ''}`}
                  onClick={expandable ? () => toggle(r.outletId) : undefined}
                  role={expandable ? 'button' : undefined}
                >
                  <td className="sm-out">
                    <Dot status={r.worstStatus} />
                    <div className="sm-out-main">
                      <b>{r.outletName}</b>
                      <span>
                        {r.shiftCount} shift{r.shiftCount === 1 ? '' : 's'}
                        {expandable ? (open ? ' \u00b7 hide' : ' \u00b7 expand') : ''}
                      </span>
                    </div>
                    {expandable && (
                      <Icon name="chevron" size={14} className="sm-chev" />
                    )}
                  </td>
                  {columns.map(col => (
                    <td key={col.key} className="sm-td">
                      <Cell cell={cellFor(r.cells, col.key)} />
                    </td>
                  ))}
                </tr>
                {open &&
                  r.shifts.map(s => (
                    <tr className={`sm-srow st-${s.state}`} key={s.shiftId}>
                      <td className="sm-sout">
                        <span
                          className="sm-state"
                          data-state={s.state}
                          onMouseEnter={e => tip.show(e, stateTip(s.state))}
                          onMouseMove={e => tip.show(e, stateTip(s.state))}
                          onMouseLeave={tip.hide}
                        >
                          {stateLabel(s.state)}
                        </span>
                        <span className="sm-sname">{s.name}</span>
                        <span className="sm-swin">{s.serviceWindow}</span>
                      </td>
                      {columns.map(col => (
                        <td key={col.key} className="sm-td">
                          <Cell cell={cellFor(s.cells, col.key)} />
                        </td>
                      ))}
                    </tr>
                  ))}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function IssueRollup({ issues }: { issues: SettingsIssueGroup[] }) {
  const { t } = useTranslation('settings');
  return (
    <div className="sm-issues">
      {issues.map(g => {
        const sev = SEV_META[g.severity];
        return (
          <div key={g.id} className="ins-card sm-issue">
            <div className="sm-issue-head">
              <span className="sm-issue-cat">
                <Icon
                  name={sev.icon}
                  size={14}
                  fill={g.severity !== 'info'}
                  style={{ color: sev.c }}
                />
                {g.title}
              </span>
              <span className="sm-issue-count">
                {g.affected.length} affected
              </span>
            </div>
            <div className="sm-issue-impact">{g.impact}</div>
            <div className="ins-action">
              <Icon
                name="check"
                size={13}
                stroke={2.3}
                style={{ marginTop: 1, color: sev.c }}
              />
              <span>{g.recommendation}</span>
            </div>
            <details className="sm-affected">
              <summary>{t('health.where')}</summary>
              <div className="sm-affected-list">
                {g.affected.map((a, i) => (
                  <span key={i} className="sm-chip">
                    {a.outletName}
                    {a.shiftName ? ` \u00b7 ${a.shiftName}` : ''}
                    {a.detail ? ` (${a.detail})` : ''}
                  </span>
                ))}
              </div>
            </details>
          </div>
        );
      })}
    </div>
  );
}

type TipState = {
  text?: string;
  content?: React.ReactNode;
  wide?: boolean;
  placement?: 'above' | 'right';
  x: number;
  y: number;
};

function TooltipLayer({ tip }: { tip: TipState | null }) {
  if (!tip || typeof document === 'undefined') {
    return null;
  }
  const isRight = tip.placement === 'right';
  const estWidth = tip.wide ? 360 : 280;
  const offset = 14;
  let left = tip.x;
  let top = tip.y;

  if (isRight) {
    left = Math.min(tip.x + offset, window.innerWidth - estWidth - 8);
    left = Math.max(8, left);
    top = Math.min(Math.max(top, 12), window.innerHeight - 12);
  } else {
    left = Math.min(Math.max(tip.x, 150), window.innerWidth - 150);
  }

  return createPortal(
    <div
      className={`sm-tip${tip.wide ? ' sm-tip-wide' : ''}${
        isRight ? ' sm-tip-right' : ''
      }`}
      style={{ left, top }}
      role="tooltip"
    >
      {tip.content ?? tip.text}
    </div>,
    document.body,
  );
}

export function SettingsHealthPanel({
  audit,
}: {
  audit?: SettingsAuditPayload;
}) {
  const { t } = useTranslation('settings');
  const [tip, setTip] = useState<TipState | null>(null);

  const show = useCallback((e: React.MouseEvent, payload?: TipPayload) => {
    if (!payload) return;
    if (typeof payload === 'string') {
      if (!payload) return;
      setTip({ text: payload, x: e.clientX, y: e.clientY });
      return;
    }
    if (!payload.text && !payload.content) return;
    setTip({
      text: payload.text,
      content: payload.content,
      wide: payload.wide,
      placement: payload.placement,
      x: e.clientX,
      y: e.clientY,
    });
  }, []);
  const hide = useCallback(() => setTip(null), []);
  const tipApi = useMemo(() => ({ show, hide }), [show, hide]);

  if (!audit || !audit.restaurants.length) {
    return null;
  }

  return (
    <TipCtx.Provider value={tipApi}>
      <div className="sec-head" style={{ marginTop: 22 }}>
        <h2>{t('health.title')}</h2>
        <span className="sh-meta">
          {audit.outletsChecked === 1
            ? t('health.meta', {
                shifts: audit.shiftsChecked,
                outlets: audit.outletsChecked,
              })
            : t('health.metaPlural', {
                shifts: audit.shiftsChecked,
                outlets: audit.outletsChecked,
              })}
        </span>
        <span
          className="prov-note"
          title={t('health.matrixTip')}
        >
          {t('health.fromSettings')}
        </span>
      </div>

      <div className="sm-subhead">{t('health.overview')}</div>
      <div className="ol-card sm-card" data-ftue="settings-matrix">
        <SettingsMatrix
          columns={audit.columns}
          restaurants={audit.restaurants}
        />
        <div className="sm-legend">
          <span>
            <Dot status="ok" />
            {t('health.healthy')}
          </span>
          <span>
            <Dot status="warn" />
            {t('health.worthLook')}
          </span>
          <span>
            <Dot status="alert" />
            {t('health.blocksBookings')}
          </span>
          {audit.benchmarks.length > 0 && (
            <span className="sm-bench">
              {t('health.fleetMedian')}{' '}
              {audit.benchmarks
                .map(b => `${b.label} ${b.median}${b.unit ?? ''}`)
                .join(' \u00b7 ')}
            </span>
          )}
        </div>
      </div>

      {audit.issueGroups.length > 0 && (
        <div data-ftue="settings-issues">
          <div className="sm-subhead">
            What to fix ({audit.issueGroups.length})
          </div>
          <IssueRollup issues={audit.issueGroups} />
        </div>
      )}

      <TooltipLayer tip={tip} />
    </TipCtx.Provider>
  );
}
